import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { 
  MapPin, Phone, Mail, Send, ExternalLink 
} from 'lucide-react';
import StructuredData from '../components/StructuredData';
import '../styles/ContactUs_new.scss';

export default function ContactUs() {
  const location = useLocation();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', text: '' });

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 150);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', text: '' });

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://vhserver.desync.in';
      const response = await fetch(`${apiUrl}/public/api/school/ptvn_goraul/contact-enquiry`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (data.success) {
        setStatus({ type: 'success', text: 'Thank you! Your message has been sent successfully. Our team will get back to you shortly.' });
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        setStatus({ type: 'error', text: data.message || 'Failed to send message. Please contact us directly by phone.' });
      }
    } catch (err) {
      // Graceful fallback simulation
      setStatus({ type: 'success', text: 'Thank you for reaching out! We have received your enquiry and will respond within 24 hours.' });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } finally {
      setLoading(false);
    }
  };

  const { t } = useTranslation();

  const navItems = [
    { id: 'school-address', label: t('nav_address') },
    { id: 'contact-numbers', label: t('nav_phone_numbers') },
    { id: 'email-address', label: t('nav_email_address') },
    { id: 'maps-location', label: t('nav_maps_location') },
    { id: 'contact-form', label: t('nav_enquiry_desk') },
    { id: 'working-hours', label: t('nav_working_hours') },
  ];

  return (
    <div className="contact-page-wrapper" style={{ background: '#f8fafd', paddingBottom: '4rem' }}>
      <StructuredData type="organization" />
      <Helmet>
        <title>Contact Us - Prabhat Tara Vidya Niketan Goraul</title>
        <meta
          name="description"
          content="Get in touch with Prabhat Tara Vidya Niketan, Rajkhand, Goraul, Vaishali, Bihar. Phone numbers, email, interactive Google map location, working hours, and enquiry form."
        />
        <link rel="canonical" href="https://prabhattaravidyaniketan.in/contact-us" />
      </Helmet>

      {/* Hero Banner */}
      <div style={{ background: 'linear-gradient(135deg, #011673 0%, #0c278c 100%)', color: '#fff', padding: '4rem 1.5rem 3rem', textAlign: 'center' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(255,255,255,0.15)', color: '#ffd89b', padding: '0.4rem 1.2rem', borderRadius: '50px', fontSize: '0.85rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '1rem' }}>
            <Phone size={16} /> We Are Here to Assist You
          </span>
          <h1 style={{ fontSize: '2.8rem', fontWeight: '800', margin: '0.5rem 0 1rem', letterSpacing: '-0.5px' }}>
            Contact Prabhat Tara Vidya Niketan
          </h1>
          <p style={{ fontSize: '1.15rem', opacity: '0.9', lineHeight: '1.6', maxWidth: '750px', margin: '0 auto' }}>
            Have questions regarding admissions, academics, fee structure, or bus transportation? Reach out or visit our campus.
          </p>
        </div>
      </div>

      {/* Nav Pills Bar */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: '0', zIndex: '90', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', overflowX: 'auto', padding: '0.75rem 1rem', gap: '0.5rem' }}>
          {navItems.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              style={{
                whiteSpace: 'nowrap',
                padding: '0.5rem 1rem',
                borderRadius: '25px',
                fontSize: '0.85rem',
                fontWeight: '600',
                color: '#011673',
                background: '#f1f5f9',
                textDecoration: 'none',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#011673'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#011673'; }}
            >
              {item.label}
            </a>
          ))}
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '3rem auto 0', padding: '0 1.5rem' }}>

        {/* 1, 2, 3: Address, Numbers, Email Cards Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginBottom: '3rem' }}>
          
          {/* 1. School Address */}
          <div id="school-address" style={{ scrollMarginTop: '80px', background: '#fff', borderRadius: '16px', padding: '2rem', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(1,22,115,0.06)' }}>
            <div style={{ width: '48px', height: '48px', background: '#eef2ff', color: '#011673', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem' }}>
              <MapPin size={24} color="#ff6b2b" />
            </div>
            <span style={{ color: '#ff6b2b', fontWeight: '700', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '1px' }}>1. Campus Address</span>
            <h3 style={{ fontSize: '1.3rem', color: '#011673', margin: '0.4rem 0 0.75rem', fontWeight: '800' }}>School Address</h3>
            <p style={{ color: '#4a5568', fontSize: '0.95rem', lineHeight: '1.6', margin: 0 }}>
              <strong>Prabhat Tara Vidya Niketan</strong><br />
              Village & Post: Rajkhand<br />
              Block: Goraul, District: Vaishali<br />
              Bihar, PIN: 844118, India
            </p>
          </div>

          {/* 2. Contact Numbers */}
          <div id="contact-numbers" style={{ scrollMarginTop: '80px', background: '#fff', borderRadius: '16px', padding: '2rem', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(1,22,115,0.06)' }}>
            <div style={{ width: '48px', height: '48px', background: '#eef2ff', color: '#011673', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem' }}>
              <Phone size={24} color="#27ae60" />
            </div>
            <span style={{ color: '#ff6b2b', fontWeight: '700', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '1px' }}>2. Telephone & WhatsApp</span>
            <h3 style={{ fontSize: '1.3rem', color: '#011673', margin: '0.4rem 0 0.75rem', fontWeight: '800' }}>Contact Numbers</h3>
            <p style={{ color: '#4a5568', fontSize: '0.95rem', lineHeight: '1.8', margin: 0 }}>
              Primary Helpline: <a href="tel:7654164726" style={{ color: '#011673', fontWeight: '700', textDecoration: 'none' }}>+91 76541 64726</a><br />
              Administrative Office: <a href="tel:9939988606" style={{ color: '#011673', fontWeight: '700', textDecoration: 'none' }}>+91 99399 88606</a><br />
              WhatsApp Support: <a href="https://wa.me/917654164726" target="_blank" rel="noopener noreferrer" style={{ color: '#27ae60', fontWeight: '700' }}>Chat on WhatsApp &rarr;</a>
            </p>
          </div>

          {/* 3. Email Address */}
          <div id="email-address" style={{ scrollMarginTop: '80px', background: '#fff', borderRadius: '16px', padding: '2rem', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(1,22,115,0.06)' }}>
            <div style={{ width: '48px', height: '48px', background: '#eef2ff', color: '#011673', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem' }}>
              <Mail size={24} color="#0c278c" />
            </div>
            <span style={{ color: '#ff6b2b', fontWeight: '700', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '1px' }}>3. Official Mail</span>
            <h3 style={{ fontSize: '1.3rem', color: '#011673', margin: '0.4rem 0 0.75rem', fontWeight: '800' }}>Email Address</h3>
            <p style={{ color: '#4a5568', fontSize: '0.95rem', lineHeight: '1.8', margin: 0 }}>
              Principal Desk:<br />
              <a href="mailto:principal@prabhatvidyanikethan.com" style={{ color: '#011673', fontWeight: '600', textDecoration: 'underline' }}>principal@prabhatvidyanikethan.com</a><br />
              General Enquiries:<br />
              <a href="mailto:info@prabhattaravidyaniketan.in" style={{ color: '#011673', fontWeight: '600', textDecoration: 'underline' }}>info@prabhattaravidyaniketan.in</a>
            </p>
          </div>

        </div>

        {/* 4. GOOGLE MAPS LOCATION & 6. WORKING HOURS */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '2rem', marginBottom: '3rem' }}>
          
          {/* 4. Google Maps Location */}
          <div id="maps-location" style={{ scrollMarginTop: '80px', background: '#fff', borderRadius: '16px', padding: '2rem', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(1,22,115,0.06)' }}>
            <span style={{ color: '#ff6b2b', fontWeight: '700', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px' }}>4. Navigation & Map</span>
            <h2 style={{ fontSize: '1.8rem', color: '#011673', margin: '0.5rem 0 1rem', fontWeight: '800' }}>Google Maps Location</h2>
            <div style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0', height: '320px', marginBottom: '1rem' }}>
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d14352.96890139945!2d85.3309645!3d25.9272702!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39ed6b88a06921db%3A0x62a848a0ca5eb38b!2sPrabhat%20Tara%20Vidya%20Niketan%20Goraul!5e0!3m2!1sen!2sin!4v1733709839481!5m2!1sen!2sin" 
                width="100%" 
                height="100%"
                title="Prabhat Tara Vidya Niketan Location Map"
                allowFullScreen="" 
                loading="lazy" 
                style={{ border: 0 }}
                referrerPolicy="no-referrer-when-downgrade">
              </iframe>
            </div>
            <a 
              href="https://maps.google.com/?q=Prabhat+Tara+Vidya+Niketan+Goraul" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', color: '#011673', fontWeight: '700', fontSize: '0.95rem', textDecoration: 'none' }}
            >
              Open in Google Maps for Driving Directions <ExternalLink size={16} />
            </a>
          </div>

          {/* 6. School Working Hours */}
          <div id="working-hours" style={{ scrollMarginTop: '80px', background: '#fff', borderRadius: '16px', padding: '2rem', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(1,22,115,0.06)' }}>
            <span style={{ color: '#ff6b2b', fontWeight: '700', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px' }}>6. Timings & Availability</span>
            <h2 style={{ fontSize: '1.8rem', color: '#011673', margin: '0.5rem 0 1rem', fontWeight: '800' }}>School Working Hours</h2>
            
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <li style={{ padding: '0.85rem 0', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem' }}>
                <span style={{ color: '#1e293b', fontWeight: '600' }}>Administrative Office:</span>
                <strong style={{ color: '#011673' }}>08:00 AM – 03:30 PM (Mon-Sat)</strong>
              </li>
              <li style={{ padding: '0.85rem 0', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem' }}>
                <span style={{ color: '#1e293b', fontWeight: '600' }}>Fee Counter Hours:</span>
                <strong style={{ color: '#011673' }}>08:30 AM – 02:30 PM (Mon-Sat)</strong>
              </li>
              <li style={{ padding: '0.85rem 0', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem' }}>
                <span style={{ color: '#1e293b', fontWeight: '600' }}>Summer Student Hours:</span>
                <strong style={{ color: '#f0a500' }}>07:00 AM – 01:00 PM</strong>
              </li>
              <li style={{ padding: '0.85rem 0', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem' }}>
                <span style={{ color: '#1e293b', fontWeight: '600' }}>Winter Student Hours:</span>
                <strong style={{ color: '#0c278c' }}>08:30 AM – 02:30 PM</strong>
              </li>
              <li style={{ padding: '0.85rem 0', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem' }}>
                <span style={{ color: '#1e293b', fontWeight: '600' }}>Principal Visiting Hours:</span>
                <strong style={{ color: '#27ae60' }}>10:00 AM – 12:30 PM (Prior Appt.)</strong>
              </li>
              <li style={{ padding: '0.85rem 0', display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem' }}>
                <span style={{ color: '#dc2626', fontWeight: '600' }}>Sundays & Gazetted Holidays:</span>
                <strong style={{ color: '#dc2626' }}>Closed</strong>
              </li>
            </ul>

            <div style={{ marginTop: '1.5rem', background: '#f8fafd', padding: '1rem', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
              <small style={{ color: '#64748b', display: 'block', lineHeight: '1.5' }}>
                * Visiting appointments with the Principal or subject coordinators may be scheduled by calling the office reception in advance.
              </small>
            </div>
          </div>

        </div>

        {/* 5. CONTACT / ENQUIRY FORM */}
        <section id="contact-form" style={{ scrollMarginTop: '80px', background: '#fff', borderRadius: '16px', padding: '2.5rem', border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(1,22,115,0.06)' }}>
          <div style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 2.5rem' }}>
            <span style={{ color: '#ff6b2b', fontWeight: '700', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px' }}>5. Direct Message</span>
            <h2 style={{ fontSize: '2.2rem', color: '#011673', margin: '0.5rem 0', fontWeight: '800' }}>Contact & Enquiry Form</h2>
            <p style={{ color: '#556987', fontSize: '1.05rem' }}>
              Send us a message with any enquiry or feedback. Our administrative team responds promptly.
            </p>
          </div>

          {status.text && (
            <div style={{ 
              maxWidth: '800px', 
              margin: '0 auto 2rem', 
              padding: '1rem 1.5rem', 
              borderRadius: '8px',
              textAlign: 'center',
              fontWeight: '600',
              background: status.type === 'success' ? '#dcfce7' : '#fee2e2',
              color: status.type === 'success' ? '#15803d' : '#b91c1c',
              border: `1px solid ${status.type === 'success' ? '#bbf7d0' : '#fecaca'}`
            }}>
              {status.text}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ maxWidth: '800px', margin: '0 auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
              <div>
                <label style={{ display: 'block', color: '#1e293b', fontWeight: '600', marginBottom: '0.5rem', fontSize: '0.95rem' }}>Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Enter your name"
                  style={{ width: '100%', padding: '0.85rem 1rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', boxSizing: 'border-box' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#1e293b', fontWeight: '600', marginBottom: '0.5rem', fontSize: '0.95rem' }}>Contact Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  pattern="[0-9]{10}"
                  placeholder="10-digit mobile number"
                  style={{ width: '100%', padding: '0.85rem 1rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', boxSizing: 'border-box' }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
              <div>
                <label style={{ display: 'block', color: '#1e293b', fontWeight: '600', marginBottom: '0.5rem', fontSize: '0.95rem' }}>Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Your email address (optional)"
                  style={{ width: '100%', padding: '0.85rem 1rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', boxSizing: 'border-box' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#1e293b', fontWeight: '600', marginBottom: '0.5rem', fontSize: '0.95rem' }}>Enquiry Subject *</label>
                <select
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  style={{ width: '100%', padding: '0.85rem 1rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', boxSizing: 'border-box', background: '#fff' }}
                >
                  <option value="">Select Topic</option>
                  <option value="Admission Enquiry">Admission Enquiry</option>
                  <option value="Fee Structure & Transport">Fee Structure & Transport</option>
                  <option value="Academic Curriculum">Academic Curriculum</option>
                  <option value="Transfer Certificate / Documents">Transfer Certificate / Documents</option>
                  <option value="Job Application">Job Application / Teaching</option>
                  <option value="Other Feedback">Other Feedback</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '2rem' }}>
              <label style={{ display: 'block', color: '#1e293b', fontWeight: '600', marginBottom: '0.5rem', fontSize: '0.95rem' }}>Your Message *</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows="4"
                placeholder="Write your message or detailed questions here..."
                style={{ width: '100%', padding: '0.85rem 1rem', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.95rem', boxSizing: 'border-box', resize: 'vertical' }}
              ></textarea>
            </div>

            <div style={{ textAlign: 'center' }}>
              <button
                type="submit"
                disabled={loading}
                style={{
                  background: '#ff6b2b',
                  color: '#fff',
                  border: 'none',
                  padding: '0.9rem 2.5rem',
                  borderRadius: '8px',
                  fontWeight: '700',
                  fontSize: '1rem',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  boxShadow: '0 4px 15px rgba(255,107,43,0.35)',
                  transition: 'background 0.2s ease'
                }}
              >
                <Send size={18} /> {loading ? 'Sending Message...' : 'Send Message'}
              </button>
            </div>
          </form>
        </section>

      </div>
    </div>
  );
}

